import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, Brain, TrendingUp, ArrowRight, Check, Clock, Mic2 } from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface VocabItem {
  term: string;
  last_failed?: string;
  mastered_on?: string;
}

interface PersonalContext {
  fact: string;
  first_mentioned?: string;
}

interface MemoryDiff {
  newMastered: VocabItem[];
  newFailed: VocabItem[];
  newContext: PersonalContext[];
  cefrLevel: string;
  nextFocus: string;
  strengths: string[];
}

interface PostSessionSummaryProps {
  /** Whether this overlay is visible */
  visible: boolean;
  /** Duration of the session in seconds */
  sessionSeconds: number;
  /** Session mode slug e.g. "free_conversation" */
  sessionMode: string;
  /** The latest memory_json fetched fresh from Supabase after memory save */
  memoryDiff: MemoryDiff | null;
  /** Target language */
  targetLanguage: string;
  /** Number of turns taken this session */
  turnCount: number;
  /** Called when user confirms "Continue to Dashboard" */
  onDone: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s > 0 ? s + "s" : ""}`.trim();
}

function cefrColor(level: string): string {
  const map: Record<string, string> = { A1: "cyan", A2: "cyan", B1: "blue", B2: "blue", C1: "violet", C2: "violet" };
  return map[level] || "cyan";
}

// ─── Confetti dots ────────────────────────────────────────────────────────────

const ConfettiDot = ({ delay, x, color }: { delay: number; x: number; color: string }) => (
  <motion.div
    className="absolute w-1.5 h-1.5 rounded-full"
    style={{ left: `${x}%`, top: "-10px", background: color }}
    initial={{ y: 0, opacity: 1 }}
    animate={{ y: 400, opacity: 0, rotate: 360 }}
    transition={{ duration: 1.8, delay, ease: "easeIn" }}
  />
);

const confettiColors = [
  "rgb(var(--accent-cyan-rgb))",
  "rgb(var(--accent-blue-rgb))",
  "rgb(var(--accent-violet-rgb))",
  "#fff",
];

// ─── Component ────────────────────────────────────────────────────────────────

const PostSessionSummary: React.FC<PostSessionSummaryProps> = ({
  visible,
  sessionSeconds,
  sessionMode,
  memoryDiff,
  targetLanguage,
  turnCount,
  onDone,
}) => {
  const [confetti] = useState(() =>
    Array.from({ length: 24 }, (_, i) => ({
      id: i,
      delay: Math.random() * 0.6,
      x: Math.random() * 100,
      color: confettiColors[i % confettiColors.length],
    }))
  );

  const modeLabel = sessionMode.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const cefrLevel = memoryDiff?.cefrLevel || "A1";
  const accentColor = cefrColor(cefrLevel);

  // Stats row
  const stats = [
    { label: "Duration", value: formatDuration(sessionSeconds), icon: Clock },
    { label: "CEFR level", value: cefrLevel, icon: TrendingUp },
    { label: "Turns", value: String(turnCount || 0), icon: Mic2 },
  ];

  // What AURA learned this session
  const newMastered = memoryDiff?.newMastered || [];
  const needsWork = memoryDiff?.newFailed || [];
  const newContext = memoryDiff?.newContext || [];
  const nextFocus = memoryDiff?.nextFocus || "";

  const hasInsights = newMastered.length > 0 || needsWork.length > 0 || newContext.length > 0;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-50 grid place-items-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="post-session-summary"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-base/85 backdrop-blur-xl" />

          {/* Confetti */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {confetti.map((c) => (
              <ConfettiDot key={c.id} delay={c.delay} x={c.x} color={c.color} />
            ))}
          </div>

          {/* Card */}
          <motion.div
            initial={{ y: 24, scale: 0.97 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 12, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[560px] rounded-3xl glass edge-glow overflow-hidden"
          >
            {/* Glow top */}
            <div
              aria-hidden
              className="absolute -top-24 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full blur-[120px]"
              style={{ background: `rgb(var(--accent-${accentColor}-rgb) / 0.3)` }}
            />

            <div className="relative z-10 p-6 sm:p-8">
              {/* Header */}
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-2xl grid place-items-center border shrink-0"
                  style={{
                    background: `rgb(var(--accent-${accentColor}-rgb) / 0.1)`,
                    borderColor: `rgb(var(--accent-${accentColor}-rgb) / 0.3)`,
                    color: `rgb(var(--accent-${accentColor}-rgb))`,
                  }}
                >
                  <Brain size={22} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
                    Session complete · {modeLabel}
                  </div>
                  <h2 className="mt-0.5 font-display text-[22px] sm:text-[26px] font-light tracking-tight text-ink leading-tight">
                    {targetLanguage} · AURA has saved it all.
                  </h2>
                </div>
              </div>

              {/* Stats strip */}
              <div className="mt-6 grid grid-cols-3 gap-3">
                {stats.map((s) => (
                  <div
                    key={s.label}
                    className="rounded-2xl border border-panel/8 bg-panel/[0.04] p-3 text-center"
                  >
                    <div className="font-display text-[24px] font-light leading-none tabular-nums text-ink">
                      {s.value}
                    </div>
                    <div className="mt-1 font-mono text-[9.5px] tracking-[0.2em] uppercase text-whisper-muted">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Memory diff section */}
              {hasInsights && (
                <div className="mt-6 space-y-4">
                  {/* Mastered vocab */}
                  {newMastered.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-aura-cyan mb-2">
                        <Check size={11} /> Mastered this session
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {newMastered.slice(0, 6).map((v) => (
                          <span
                            key={v.term}
                            className="rounded-full border border-aura-cyan/30 bg-aura-cyan/[0.08] px-3 py-1 font-mono text-[11px] tracking-[0.1em] text-ink"
                          >
                            {v.term}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Vocab gaps flagged */}
                  {needsWork.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted mb-2">
                        <Sparkles size={11} /> Flagged for next session
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {needsWork.slice(0, 4).map((v) => (
                          <span
                            key={v.term}
                            className="rounded-full border border-panel/15 bg-panel/[0.05] px-3 py-1 font-mono text-[11px] tracking-[0.1em] text-whisper"
                          >
                            {v.term}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Personal context learned */}
                  {newContext.length > 0 && (
                    <div className="rounded-2xl border border-aura-violet/20 bg-aura-violet/[0.05] p-3">
                      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-aura-violet mb-1.5">
                        She now knows
                      </div>
                      {newContext.slice(0, 2).map((c, i) => (
                        <div key={i} className="text-[13.5px] text-ink/90">
                          · {c.fact}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* No insights fallback (new user) */}
              {!hasInsights && (
                <div className="mt-6 rounded-2xl border border-panel/8 bg-panel/[0.03] p-4 text-center">
                  <div className="font-serif italic text-[16px] text-ink/80">
                    Great first session. AURA is beginning to map your cognitive profile.
                  </div>
                </div>
              )}

              {/* Next focus */}
              {nextFocus && nextFocus !== "Your first conversation with AURA" && (
                <div className="mt-4 rounded-2xl border border-aura-blue/20 bg-aura-blue/[0.05] p-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg grid place-items-center bg-aura-blue/10 border border-aura-blue/20 text-aura-blue shrink-0">
                    <Brain size={14} />
                  </div>
                  <div>
                    <div className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-whisper-muted">
                      next session focus
                    </div>
                    <div className="text-[13.5px] text-ink mt-0.5">{nextFocus}</div>
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="mt-7 flex items-center gap-3">
                <button
                  onClick={onDone}
                  className="btn-primary flex-1 justify-center py-3 text-[12px]"
                  data-testid="post-session-done"
                >
                  Continue to dashboard <ArrowRight size={14} />
                </button>
              </div>

              <p className="mt-3 text-center font-mono text-[10px] tracking-[0.18em] uppercase text-whisper-muted">
                {targetLanguage} · {cefrLevel} · memory saved ✓
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PostSessionSummary;
